import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { events } from '../client';

interface SimulationSseEvent {
  type: 'state_changed' | 'task_updated' | 'feature_completed' | 'feature_flags_changed';
  simulation_id: string;
  task_id?: string;
  feature_id?: string;
}

/**
 * Side-effect-only hook: opens an SSE connection to the simulation's event
 * stream and invalidates the appropriate React Query caches when the server
 * pushes an update. Mount once per simulation view (in the layout component).
 *
 * Accepts simId as a parameter (rather than reading from SimulationContext)
 * because this hook is mounted in the layout component that creates the context.
 */
export function useSimulationEvents(simId: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const { stream } = await events({
          path: { sim_id: simId },
          signal: controller.signal,
        });

        for await (const raw of stream) {
          const event = raw as SimulationSseEvent;

          switch (event.type) {
            case 'state_changed':
              queryClient.invalidateQueries({ queryKey: ['simulation', simId] });
              break;

            case 'task_updated':
              if (event.task_id) {
                queryClient.invalidateQueries({ queryKey: ['task', simId, event.task_id] });
              }
              queryClient.invalidateQueries({ queryKey: ['team-tasks'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['features'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['features-with-team-context'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['dashboardData', simId] });
              break;

            case 'feature_completed':
              queryClient.invalidateQueries({ queryKey: ['features'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['features-with-team-context'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['dashboardData', simId] });
              if (event.feature_id) {
                queryClient.invalidateQueries({ queryKey: ['feature', simId, event.feature_id] });
              }
              break;

            case 'feature_flags_changed':
              queryClient.invalidateQueries({ queryKey: ['feature-flags', simId] });
              break;
          }
        }
      } catch {
        // AbortError on unmount — expected; other errors handled by SSE client retry logic
      }
    })();

    return () => controller.abort();
  }, [simId, queryClient]);
}
